import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { formatAUD, formatDateAU } from '../../../../shared/format';
import {
  addDaysIso, applyBookQuery, cellValue, isFilterActive, sumColumn,
  type BookColumn, type ColumnFilter, type FilterMap, type SortSpec,
} from '../../../../shared/bookGrid';
import type { ComplianceState, DashboardRow } from '../../../../shared/types';
import { FilterPopover } from './FilterPopover';
import { editTargetFor, type EditTarget } from '../../../../shared/bookEdit';

const SUMMED = new Set(['tnw', 'cash', 'openItems', 'proposedTrades', 'sellsCount']);

/**
 * The Excel-style book: sticky header + sticky client column, a filter button
 * on every header, red/green compliance cells, and a subtotal row that follows
 * the active filter (Excel's SUBTOTAL behaviour).
 */
export function BookGrid({
  rows, allRows, columns, today, q, filters, sort, onFilters, onSort, onEdit, onLogCall, selected, onSelect,
}: {
  /** Bulk selection (client ids). When onSelect is set, rows get checkboxes. */
  selected?: ReadonlySet<string>;
  onSelect?: (ids: string[], on: boolean) => void;
  /** When set, each client name gets a Log call button. */
  onLogCall?: (row: DashboardRow, anchor: HTMLElement) => void;
  /** When set, compliance / Call Due / Review Date cells become editable buttons. */
  onEdit?: (row: DashboardRow, target: EditTarget, anchor: HTMLElement) => void;
  /** Rows after search + filters + sort. */
  rows: DashboardRow[];
  /** Every row (for building each column's value list). */
  allRows: DashboardRow[];
  columns: BookColumn[];
  today: string;
  q: string;
  filters: FilterMap;
  sort: SortSpec;
  onFilters: (next: FilterMap) => void;
  onSort: (next: SortSpec) => void;
}) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState<{ key: string; anchor: HTMLElement } | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);

  const openColumn = open ? columns.find((c) => c.key === open.key) : undefined;
  // Excel offers only values reachable under the OTHER columns' filters.
  const popoverRows = useMemo(() => {
    if (!open) return [];
    const others = { ...filters };
    delete others[open.key];
    return applyBookQuery(allRows, { q, filters: others }, today);
  }, [open, filters, allRows, q, today]);

  function setFilter(key: string, f: ColumnFilter | undefined) {
    const next = { ...filters };
    if (f) next[key] = f; else delete next[key];
    onFilters(next);
  }

  function headerSort(key: string) {
    onSort({ key, dir: sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc' });
  }

  return (
    <div className="qa-book-wrap" data-testid="book-grid">
      <table className="qa-book">
        <thead>
          <tr>
            {columns.map((c, i) => {
              const active = isFilterActive(filters[c.key]);
              const sorted = sort.key === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={[
                    c.vertical ? 'is-vertical' : '',
                    i === 0 ? 'is-sticky' : '',
                    c.accent ? 'is-accent' : '',
                    c.kind === 'money' || c.kind === 'int' ? 'is-num' : '',
                  ].join(' ')}
                >
                  <div className="qa-book-th">
                    {i === 0 && onSelect && (
                      <input
                        type="checkbox"
                        className="qa-book-check"
                        aria-label="Select all visible clients"
                        checked={rows.length > 0 && rows.every((r) => selected?.has(r.clientId))}
                        ref={(el) => { if (el) el.indeterminate = rows.some((r) => selected?.has(r.clientId)) && !rows.every((r) => selected?.has(r.clientId)); }}
                        onChange={(e) => onSelect(rows.map((r) => r.clientId), e.target.checked)}
                      />
                    )}
                    <button type="button" className="qa-book-th__label" onClick={() => headerSort(c.key)} title={`Sort by ${c.label}`}>
                      <span>{c.label}</span>
                      {sorted && <span className="text-rust" aria-hidden="true">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
                    </button>
                    <button
                      type="button"
                      className={`qa-book-th__filter ${active ? 'is-active' : ''}`}
                      aria-label={`Filter ${c.label}${active ? ' (filtered)' : ''}`}
                      aria-haspopup="dialog"
                      aria-expanded={open?.key === c.key}
                      onClick={(e) => setOpen(open?.key === c.key ? null : { key: c.key, anchor: e.currentTarget })}
                    >
                      {active ? <FunnelIcon /> : <CaretIcon />}
                    </button>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.clientId}
              className={[highlight === r.clientId ? 'is-selected' : '', selected?.has(r.clientId) ? 'is-checked' : ''].join(' ')}
              onClick={() => setHighlight(r.clientId)}
              onDoubleClick={() => navigate(`/clients/${r.clientId}`)}
            >
              {columns.map((c, i) => (
                <Cell key={c.key} row={r} column={c} today={today} sticky={i === 0} onEdit={onEdit} onLogCall={onLogCall} checked={selected?.has(r.clientId)} onSelect={onSelect} />
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="qa-book-empty">No clients match these filters.</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            {columns.map((c, i) => (
              <td key={c.key} className={`${i === 0 ? 'is-sticky' : ''} ${c.kind === 'money' || c.kind === 'int' ? 'is-num' : ''}`}>
                {i === 0
                  ? `${rows.length} of ${allRows.length} clients`
                  : SUMMED.has(c.key) || c.key.endsWith(':value') || c.key.endsWith(':gain')
                    ? c.kind === 'money' ? formatAUD(sumColumn(rows, c.key, today)) : sumColumn(rows, c.key, today)
                    : ''}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>

      {open && openColumn && (
        <FilterPopover
          column={openColumn}
          anchor={open.anchor}
          rows={popoverRows}
          today={today}
          filter={filters[open.key]}
          sort={sort}
          onFilter={(f) => setFilter(open.key, f)}
          onSort={(dir) => onSort({ key: open.key, dir })}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function Cell({ row, column, today, sticky, onEdit, onLogCall, checked, onSelect }: {
  row: DashboardRow; column: BookColumn; today: string; sticky: boolean;
  onEdit?: (row: DashboardRow, target: EditTarget, anchor: HTMLElement) => void;
  onLogCall?: (row: DashboardRow, anchor: HTMLElement) => void;
  checked?: boolean;
  onSelect?: (ids: string[], on: boolean) => void;
}) {
  const v = cellValue(row, column.key, today);
  const target = onEdit ? editTargetFor(column.key) : null;
  /** Wrap cell content in a full-cell button when the cell is editable. */
  const editable = (content: ReactNode, label: string) => target && onEdit ? (
    <button
      type="button"
      className="qa-book-cellbtn"
      aria-label={`Edit ${column.label} for ${row.sortName ?? row.name}: ${label}`}
      onClick={(e) => { e.stopPropagation(); onEdit(row, target, e.currentTarget); }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {content}
    </button>
  ) : content;

  if (column.key === 'name') {
    return (
      <th scope="row" className="is-sticky qa-book-name">
        <span className="flex items-center justify-between gap-2">
          {onSelect && (
            <input
              type="checkbox"
              className="qa-book-check"
              aria-label={`Select ${row.sortName ?? row.name}`}
              checked={!!checked}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onSelect([row.clientId], e.target.checked)}
            />
          )}
          <Link to={`/clients/${row.clientId}`} className="flex-1 min-w-0 truncate">{row.sortName ?? row.name}</Link>
          {onLogCall && (
            <button
              type="button"
              className="qa-book-callbtn"
              aria-label={`Log a call with ${row.sortName ?? row.name}`}
              title="Log a call"
              onClick={(e) => { e.stopPropagation(); onLogCall(row, e.currentTarget); }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <PhoneIcon />
            </button>
          )}
        </span>
      </th>
    );
  }

  if (column.kind === 'compliance') {
    const s = (v ?? 'unknown') as ComplianceState;
    const label = s === 'pass' ? 'current' : s === 'outstanding' ? 'outstanding' : 'no date on file';
    return (
      <td className={`qa-book-tick is-${s}`} title={`${column.label}: ${label}${target ? ' (click to update)' : ''}`}>
        {editable(<span aria-label={target ? undefined : `${column.label} ${label}`}>{s === 'pass' ? '✓' : s === 'outstanding' ? '✗' : '?'}</span>, label)}
      </td>
    );
  }

  const cls = [sticky ? 'is-sticky' : '', column.accent ? 'is-accent' : ''];

  if (column.kind === 'money') {
    const n = typeof v === 'number' ? v : null;
    cls.push('is-num');
    if (n != null && n < 0) cls.push('is-neg');
    return <td className={cls.join(' ')}>{n == null ? '' : n === 0 ? '-' : formatMoney(n)}</td>;
  }

  if (column.kind === 'int') {
    cls.push('is-num');
    if (column.key === 'openItems' && typeof v === 'number' && v > 0) cls.push('is-warn');
    return <td className={cls.join(' ')}>{v ?? ''}</td>;
  }

  if (column.kind === 'date') {
    const d = typeof v === 'string' ? v.slice(0, 10) : null;
    if (column.key === 'callDue' && d) {
      if (d < today) cls.push('is-overdue');
      else if (d <= addDaysIso(today, 7)) cls.push('is-soon');
    }
    if (column.key === 'reviewDue' && d && d < today) cls.push('is-overdue');
    if (column.key === 'consentDeadline' && row.consentStatus === 'lapsed') cls.push('is-overdue');
    else if (column.key === 'consentDeadline' && (row.consentStatus === 'renewalOpen' || row.consentStatus === 'dueSoon')) cls.push('is-soon');
    if (column.key === 'nextBirthday') {
      if (d && d <= addDaysIso(today, 14)) cls.push('is-soon');
      return <td className={cls.join(' ')}>{d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : ''}</td>;
    }
    return <td className={cls.join(' ')}>{editable(d ? formatDateAU(d) : '', d ? formatDateAU(d) : 'blank')}</td>;
  }

  if (column.key === 'consentStatus' && row.consentStatus) cls.push(`qa-consent-${row.consentStatus}`);
  if (column.key === 'consentDeadline' && row.consentStatus && ['renewalOpen', 'lapsed'].includes(row.consentStatus)) cls.push('is-overdue');
  return <td className={cls.join(' ')}>{v ?? ''}</td>;
}

/** Whole dollars, negatives in brackets like the legacy sheet. */
function formatMoney(n: number): string {
  const s = formatAUD(Math.abs(n));
  return n < 0 ? `(${s})` : s;
}

function PhoneIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.1 9.9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function CaretIcon() {
  return <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>;
}
function FunnelIcon() {
  return <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M0.5 1h9L6 5.2V9L4 8V5.2z" fill="currentColor" /></svg>;
}
